import { useCallback } from "react"
import type { ClientGroup } from "@stockline/shared/types/crm/client-group"
import type { Partner } from "@stockline/shared/types/crm/partner"
import { CLIENT_GROUP_TABLE } from "../../../lib/offline/resources/crm/client-group/db"
import { PARTNER_TABLE } from "../../../lib/offline/resources/crm/partner/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

export interface CreateClientGroupInput {
  name: string
  color?: string | null
  description?: string | null
  aiContext?: string | null
}

export interface UpdateClientGroupInput {
  name?: string
  color?: string | null
  description?: string | null
  aiContext?: string | null
}

/**
 * Offline-first mutation hook for client groups. Mirrors
 * `usePartnerContactMutations`: optimistic Dexie write → change-queue enqueue
 * → push-batch via the SyncEngine. The server publishes
 * `client-group.{created,updated,deleted}` events that fan back through SSE
 * to every connected client. Errors during the network push are handled by
 * the change-queue's retry/backoff logic; the optimistic Dexie row stays in
 * place until the next pull reconciles with server-authoritative shapes.
 *
 * `addPartnerToGroup` / `removePartnerFromGroup` reuse the partner
 * push-batch path: read the partner's current memberships from Dexie,
 * adjust, and push the new clientGroupIds[] through usePartnerMutations.
 * The server runs an atomic delete + insert against partner_client_groups
 * in the same transaction as the partner UPDATE.
 */
export function useClientGroupMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const createClientGroup = useCallback(
    async (input: CreateClientGroupInput): Promise<ClientGroup> => {
      const id = crypto.randomUUID()
      const now = new Date().toISOString()

      const group: ClientGroup = {
        id,
        companyId: "",
        name: input.name,
        color: input.color ?? null,
        description: input.description ?? null,
        aiContext: input.aiContext ?? null,
        deletedAt: null,
        syncId: 0,
        createdAt: now,
        updatedAt: now,
      }

      await storage.put(CLIENT_GROUP_TABLE, group)
      await changeQueue.enqueue("client-groups", {
        op: "create",
        id,
        data: group,
      })
      void engine.pushChanges("client-groups")

      return group
    },
    [storage, changeQueue, engine]
  )

  const updateClientGroup = useCallback(
    async (id: string, input: UpdateClientGroupInput): Promise<void> => {
      const existing = await storage.get<ClientGroup>(CLIENT_GROUP_TABLE, id)
      if (!existing) return

      const updated: ClientGroup = {
        ...existing,
        ...input,
        updatedAt: new Date().toISOString(),
      }

      await storage.put(CLIENT_GROUP_TABLE, updated)
      await changeQueue.enqueue("client-groups", {
        op: "update",
        id,
        data: input,
      })
      void engine.pushChanges("client-groups")
    },
    [storage, changeQueue, engine]
  )

  const deleteClientGroup = useCallback(
    async (id: string): Promise<void> => {
      await storage.delete(CLIENT_GROUP_TABLE, id)
      await changeQueue.enqueue("client-groups", {
        op: "delete",
        id,
      })
      void engine.pushChanges("client-groups")
    },
    [storage, changeQueue, engine]
  )

  /**
   * Adjust a partner's membership in `groupId` by reading the current
   * Dexie partner row, computing the new clientGroupIds[], and pushing the
   * full set through the partner push-batch path. No-op if the partner is
   * already in/out of the target state.
   *
   * The read-modify-write pair (storage get → array splice → storage put) is
   * wrapped in a `storage.transaction` so concurrent calls for the same
   * partner serialize at the IndexedDB layer instead of racing. Without the
   * transaction, two simultaneous calls (e.g. add to group A and add to
   * group B fired back-to-back from the UI) would both read the same
   * `clientGroupIds[]` snapshot and the second `put` would overwrite the
   * first. The change-queue enqueue stays outside — the queue has its own
   * persistence and folding it in would force the tx to span two tables
   * with no concurrency benefit.
   */
  const setPartnerGroupMembership = useCallback(
    async (
      partnerId: string,
      groupId: string,
      shouldBeMember: boolean
    ): Promise<void> => {
      const next = await storage.transaction(
        [PARTNER_TABLE],
        async (): Promise<string[] | null> => {
          const partner = await storage.get<Partner>(PARTNER_TABLE, partnerId)
          if (!partner) return null

          const current = partner.clientGroupIds ?? []
          const isMember = current.includes(groupId)
          if (isMember === shouldBeMember) return null

          const nextIds = shouldBeMember
            ? [...current, groupId]
            : current.filter((id) => id !== groupId)

          const updated: Partner = {
            ...partner,
            clientGroupIds: nextIds,
            updatedAt: new Date().toISOString(),
          }
          await storage.put(PARTNER_TABLE, updated)
          return nextIds
        }
      )

      if (next === null) return

      await changeQueue.enqueue("partners", {
        op: "update",
        id: partnerId,
        data: { clientGroupIds: next },
      })
      void engine.pushChanges("partners")
    },
    [storage, changeQueue, engine]
  )

  const addPartnerToGroup = useCallback(
    async (partnerId: string, groupId: string): Promise<void> => {
      await setPartnerGroupMembership(partnerId, groupId, true)
    },
    [setPartnerGroupMembership]
  )

  const removePartnerFromGroup = useCallback(
    async (partnerId: string, groupId: string): Promise<void> => {
      await setPartnerGroupMembership(partnerId, groupId, false)
    },
    [setPartnerGroupMembership]
  )

  return {
    createClientGroup,
    updateClientGroup,
    deleteClientGroup,
    addPartnerToGroup,
    removePartnerFromGroup,
  }
}
